import { useState } from 'react'
import { Reply } from 'lucide-react'

import type { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { Button } from '@/components/ui/button'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'
import { useGetEmailThread } from '@/stores/emails.store'
import { threadCustomerAddresses } from './email-addresses'
import { EmailComposeSheet } from './email-compose-sheet'
import { NeverCaptureSenderButton } from './never-capture-sender-button'
import { ThreadAddressSuggestions } from './thread-address-suggestions'

export { threadCustomerAddresses }

interface ThreadReplyProps {
  organizationId: string
  parentType: ParentTypeEnum
  parentId: string
  threadId: string
}

/**
 * Reply action for an expanded conversation, plus the "Came in on" line. A
 * reply sends from a connected inbox, so it needs the EmailInbox add-on.
 * Captured threads also get "Never capture", and address suggestions for
 * linked contacts; both gate themselves.
 */
export function ThreadReply({ organizationId, parentType, parentId, threadId }: ThreadReplyProps) {
  const { hasFeature: hasEmailInboxFeature } = useHasEmailInboxFeature()
  const [open, setOpen] = useState(false)
  const { thread, currentUserDraft } = useGetEmailThread(threadId, { enabled: hasEmailInboxFeature })

  if (!hasEmailInboxFeature || !thread) return null

  const cameInOnEmail = thread.connectedMailboxEmail || undefined
  const customers = threadCustomerAddresses(thread.participants)

  return (
    <>
      <ThreadAddressSuggestions organizationId={organizationId} threadId={threadId} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{cameInOnEmail ? `Came in on ${cameInOnEmail}` : ''}</span>
        <div className="flex flex-wrap items-center gap-2">
          <NeverCaptureSenderButton organizationId={organizationId} thread={thread} />
          <Button
            type="button"
            variant="outline"
            size="sm"
           
            onClick={() => setOpen(true)}
            disabled={customers.length === 0}
          >
            <Reply className="h-3.5 w-3.5" />
            Reply
          </Button>
        </div>
        <EmailComposeSheet
          mode="reply"
          organizationId={organizationId}
          parentType={parentType}
          parentId={parentId}
          inquiryId={thread.inquiryId}
          contactId={thread.contactId}
          threadId={threadId}
          toEmail={customers.join(', ')}
          cameInOnEmail={cameInOnEmail}
          currentUserDraft={currentUserDraft}
          existingThreadSubject={thread.subject}
          open={open}
          onOpenChange={setOpen}
        />
      </div>
    </>
  )
}
